import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Search, Loader, Newspaper, Bell, Calendar, BookOpen, FileText, X, ArrowRight } from 'lucide-react';
import { supabase } from '../../supabaseClient';
import { searchStaticPages } from '../../data/sitePages';

const DB_CATEGORY_CONFIG = {
  haberler:    { label: 'Haberler',    icon: Newspaper,  color: '#7B0D1E' },
  duyurular:   { label: 'Duyurular',   icon: Bell,       color: '#1d4ed8' },
  etkinlikler: { label: 'Etkinlikler', icon: Calendar,   color: '#059669' },
  yayinlar:    { label: 'Yayınlar',    icon: BookOpen,   color: '#7c3aed' },
};

function getResultLink(type, item) {
  if (type === 'haberler') {
    if (item.link?.startsWith('http')) return { external: item.link };
    return { internal: `/haberler/${item.slug || item.id}` };
  }
  if (type === 'duyurular') {
    if (item.link?.startsWith('http')) return { external: item.link };
    return { internal: `/duyurular/${item.slug || item.id}` };
  }
  if (type === 'etkinlikler') return { internal: '/etkinlikler' };
  if (type === 'yayinlar') {
    if (item.link?.startsWith('http')) return { external: item.link };
    return null;
  }
  return null;
}

async function searchDB(query) {
  const q = query.trim();
  if (!q) return {};
  const [haberler, duyurular, etkinlikler, yayinlar] = await Promise.all([
    supabase.from('haberler').select('id,baslik,ozet,tarih,resim_url,slug,link')
      .or(`baslik.ilike.%${q}%,ozet.ilike.%${q}%`).order('sira',{ascending:true}).limit(4),
    supabase.from('duyurular').select('id,title,summary,date,slug,link')
      .or(`title.ilike.%${q}%,summary.ilike.%${q}%`).order('sira',{ascending:true}).limit(4),
    supabase.from('etkinlikler').select('id,baslik,tarih,konum')
      .ilike('baslik',`%${q}%`).order('sira',{ascending:true}).limit(3),
    supabase.from('yayinlar').select('id,baslik,yazarlar,tarih,link')
      .or(`baslik.ilike.%${q}%,yazarlar.ilike.%${q}%`).order('sira',{ascending:true}).limit(3),
  ]);
  return {
    haberler:    haberler.data    || [],
    duyurular:   duyurular.data   || [],
    etkinlikler: etkinlikler.data || [],
    yayinlar:    yayinlar.data    || [],
  };
}

function highlight(text, query) {
  if (!text || !query) return text || '';
  const escaped = query.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  const parts = text.split(new RegExp(`(${escaped})`, 'gi'));
  return parts.map((part, i) =>
    part.toLowerCase() === query.toLowerCase()
      ? <mark key={i} className="bg-yellow-100 text-yellow-900 rounded px-0.5 font-semibold not-italic">{part}</mark>
      : part
  );
}

// ─── Ana bileşen ────────────────────────────────────────────────────────────
// variant: 'navbar' (kompakt) | 'hero' (büyük, anasayfa)
export default function SiteSearch({ placeholder = 'Sitede ara…', variant = 'navbar', className = '' }) {
  const [query, setQuery]     = useState('');
  const [dbResults, setDB]    = useState({});
  const [pages, setPages]     = useState([]);
  const [loading, setLoading] = useState(false);
  const [open, setOpen]       = useState(false);
  const navigate  = useNavigate();
  const wrapRef   = useRef(null);
  const inputRef  = useRef(null);
  const debounce  = useRef(null);

  useEffect(() => {
    const handler = (e) => { if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  useEffect(() => {
    const handler = (e) => { if (e.key === 'Escape') { setOpen(false); setQuery(''); } };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, []);

  const runSearch = useCallback(async (q) => {
    if (!q.trim()) { setDB({}); setPages([]); setOpen(false); return; }
    setLoading(true);
    setOpen(true);
    const [dbData, staticPages] = await Promise.all([searchDB(q), Promise.resolve(searchStaticPages(q))]);
    setDB(dbData);
    setPages(staticPages);
    setLoading(false);
  }, []);

  const handleChange = (e) => {
    const val = e.target.value;
    setQuery(val);
    clearTimeout(debounce.current);
    debounce.current = setTimeout(() => runSearch(val), 300);
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (query.trim()) runSearch(query);
  };

  const handleClear = () => { setQuery(''); setDB({}); setPages([]); setOpen(false); inputRef.current?.focus(); };

  const handleSelectDB = (type, item) => {
    const link = getResultLink(type, item);
    setOpen(false); setQuery(''); setDB({}); setPages([]);
    if (!link) return;
    if (link.external) window.open(link.external, '_blank', 'noopener');
    else navigate(link.internal);
  };

  const handleSelectPage = (page) => {
    setOpen(false); setQuery(''); setDB({}); setPages([]);
    navigate(page.url);
  };

  const dbTotal  = Object.values(dbResults).reduce((s, a) => s + a.length, 0);
  const hasAny   = dbTotal > 0 || pages.length > 0;

  // ── Stil değişkenleri ───
  const isHero = variant === 'hero';
  const inputCls = isHero
    ? 'w-full bg-white/10 backdrop-blur-md border border-white/30 rounded-2xl pl-12 pr-10 py-4 text-base text-white placeholder-white/60 focus:outline-none focus:bg-white/20 focus:border-white/60 transition-all'
    : 'w-full bg-gray-50 border border-gray-200 rounded-xl pl-10 pr-8 py-2.5 text-[13px] text-gray-700 placeholder-gray-400 focus:outline-none focus:bg-white focus:border-[#7B0D1E]/40 focus:ring-2 focus:ring-[#7B0D1E]/10 transition-all';
  const iconSize = isHero ? 18 : 14;
  const iconCls  = isHero ? 'text-white/60' : 'text-gray-400';
  const clearCls = isHero ? 'text-white/40 hover:text-white/80' : 'text-gray-300 hover:text-gray-500';

  return (
    <div ref={wrapRef} className={`relative ${className}`}>
      <form onSubmit={handleSubmit}>
        <div className="relative">
          <Search size={iconSize} className={`absolute left-3.5 top-1/2 -translate-y-1/2 pointer-events-none ${iconCls}`} />
          <input
            ref={inputRef}
            type="text"
            value={query}
            onChange={handleChange}
            onFocus={() => { if (query.trim() && hasAny) setOpen(true); }}
            placeholder={placeholder}
            className={inputCls}
          />
          {query && (
            <button type="button" onClick={handleClear} className={`absolute right-3 top-1/2 -translate-y-1/2 ${clearCls}`}>
              <X size={iconSize - 2} />
            </button>
          )}
        </div>
      </form>

      {/* Dropdown */}
      {open && query.trim() && (
        <div className="absolute top-full left-0 right-0 mt-2 bg-white border border-gray-100 rounded-2xl shadow-2xl z-[9999] overflow-hidden max-h-[520px] overflow-y-auto">
          {loading ? (
            <div className="flex items-center gap-2 px-4 py-4 text-gray-400 text-sm">
              <Loader size={14} className="animate-spin" /> Aranıyor…
            </div>
          ) : !hasAny ? (
            <div className="px-4 py-6 text-center text-gray-400 text-sm">
              <Search size={28} className="mx-auto mb-2 opacity-20" />
              "<span className="font-medium text-gray-600">{query}</span>" için sonuç bulunamadı.
            </div>
          ) : (
            <div className="py-2">

              {/* ── Dinamik DB sonuçları ─────────────────────────── */}
              {Object.entries(DB_CATEGORY_CONFIG).map(([type, cfg]) => {
                const items = dbResults[type];
                if (!items || items.length === 0) return null;
                const Icon = cfg.icon;
                return (
                  <div key={type}>
                    <div className="flex items-center gap-2 px-4 py-1.5 mt-1">
                      <Icon size={11} style={{ color: cfg.color }} />
                      <span className="text-[10px] font-black uppercase tracking-widest" style={{ color: cfg.color }}>{cfg.label}</span>
                    </div>
                    {items.map(item => {
                      const title = item.baslik || item.title || '';
                      const sub   = item.ozet || item.summary || item.yazarlar || item.konum || '';
                      const date  = item.tarih || item.date || '';
                      return (
                        <button key={item.id} onClick={() => handleSelectDB(type, item)}
                          className="w-full text-left px-4 py-2.5 hover:bg-gray-50 transition-colors flex items-start gap-3 group">
                          <div className="flex-1 min-w-0">
                            <p className="text-[13px] font-semibold text-gray-800 leading-snug truncate group-hover:text-[#7B0D1E] transition-colors">
                              {highlight(title, query)}
                            </p>
                            {sub && <p className="text-[11px] text-gray-400 mt-0.5 line-clamp-1">{highlight(sub, query)}</p>}
                          </div>
                          {date && <span className="text-[10px] text-gray-300 flex-shrink-0 mt-0.5">{date}</span>}
                        </button>
                      );
                    })}
                  </div>
                );
              })}

              {/* ── Statik sayfa sonuçları ───────────────────────── */}
              {pages.length > 0 && (
                <div>
                  <div className="flex items-center gap-2 px-4 py-1.5 mt-1">
                    <FileText size={11} style={{ color: '#6b7280' }} />
                    <span className="text-[10px] font-black uppercase tracking-widest text-gray-400">Sayfalar</span>
                  </div>
                  {pages.map(page => (
                    <button key={page.url} onClick={() => handleSelectPage(page)}
                      className="w-full text-left px-4 py-2.5 hover:bg-gray-50 transition-colors flex items-center gap-3 group">
                      <div className="flex-1 min-w-0">
                        <p className="text-[13px] font-semibold text-gray-800 leading-snug group-hover:text-[#7B0D1E] transition-colors">
                          {highlight(page.title, query)}
                        </p>
                        <p className="text-[11px] text-gray-400 mt-0.5 line-clamp-1">{page.description}</p>
                      </div>
                      <div className="flex items-center gap-1.5 flex-shrink-0">
                        <span className="text-[10px] bg-gray-100 text-gray-500 px-2 py-0.5 rounded-full">{page.category}</span>
                        <ArrowRight size={11} className="text-gray-300 group-hover:text-[#7B0D1E] transition-colors" />
                      </div>
                    </button>
                  ))}
                </div>
              )}

              {/* Alt bilgi */}
              <div className="border-t border-gray-50 mt-2 px-4 py-2.5 flex items-center justify-between">
                <p className="text-[11px] text-gray-400">
                  <span className="font-semibold text-gray-600">{dbTotal + pages.length}</span> sonuç bulundu
                </p>
                <p className="text-[10px] text-gray-300">ESC ile kapat</p>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
